import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/config";

import PrimaryButton from "../../components/Buttons/PrimaryButton";
import SecondaryButton from "../../components/Buttons/SecondaryButton";
import UserNavbar from "../../components/UserNavbar";
import Footer from "../../components/Footer";

import { ArrowRight } from "lucide-react";

const Profile: React.FC = () => {
  const { user } = useAuth();
  const isPsdiUser = user?.uid === "keycloak-user";

  const [newName, setNewName] = useState("");
  const [nameError, setNameError] = useState("");
  const [nameSuccess, setNameSuccess] = useState("");
  const [nameLoading, setNameLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const handleNameChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError("");
    setNameSuccess("");

    if (!user) {
      setNameError("User not authenticated");
      return;
    }

    if (!newName.trim()) {
      setNameError("Please enter a name");
      return;
    }

    setNameLoading(true);

    try {
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, {
        name: newName.trim(),
      });

      setNameSuccess("Name updated successfully!");
      setNewName("");

      setTimeout(() => {
        setNameModalOpen(false);
        setNameSuccess("");
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      console.error("Error updating name:", error);
      setNameError(error.message || "Failed to update name");
    } finally {
      setNameLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!user || !user.email) {
      setPasswordError("User not authenticated");
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Please fill in all fields");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    setPasswordLoading(true);

    try {
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      setPasswordSuccess("Password updated successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      setTimeout(() => {
        setPasswordModalOpen(false);
        setPasswordSuccess("");
      }, 1500);
    } catch (error: any) {
      console.error("Error updating password:", error);
      if (error.code === "auth/invalid-credential") {
        setPasswordError("Current password is incorrect");
      } else if (error.code === "auth/weak-password") {
        setPasswordError("Password is too weak");
      } else if (error.code === "auth/requires-recent-login") {
        setPasswordError(
          "Please log out and log back in before changing your password"
        );
      } else {
        setPasswordError(error.message || "Failed to update password");
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto max-w-2xl mt-20 mb-20 px-4">
        <h2 className="text-2xl font-bold">My Profile</h2>
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <UserNavbar />
      <div className="flex-1 w-1/2 max-[600px]:w-full max-[600px]:px-4 mx-auto mt-20 mb-20 text-left">
        <h3 className="text-2xl font-semibold">Profile</h3>
        <p className="text-gray-700">Manage your profile settings</p>
        <hr className="my-4 border-gray-300" />

        {/* account details */}
        <h6 className="text-base font-semibold mt-4">Account details</h6>
        <div className="border border-gray-300 rounded p-4 mt-3">
          <div className="flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1 mb-4">
            <div className="flex-1">Name</div>
            <div className="flex items-center gap-2">
              <span className="text-gray-700">{user.name || "Not set"}</span>
              {!isPsdiUser && (
                <button
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  onClick={() => setNameModalOpen(true)}
                >
                  <ArrowRight size={18} />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
            <div className="flex-1">Email</div>
            <span className="text-gray-700 max-[600px]:break-all">{user.email}</span>
          </div>
        </div>

        {/* security */}
        {!isPsdiUser && (
          <>
            <h6 className="text-base font-semibold mt-6">Security</h6>
            <div className="border border-gray-300 rounded p-4 mt-3">
              <div className="flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-3">
                <div>
                  <span className="font-medium">Password</span>
                  <p className="text-sm text-gray-500 mt-1">
                    Update your password to keep your account secure
                  </p>
                </div>
                <button
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors text-sm font-medium max-[600px]:w-full"
                  onClick={() => setPasswordModalOpen(true)}
                >
                  Change password
                </button>
              </div>
            </div>
          </>
        )}

        {/* legal */}
        <h6 className="text-base font-semibold mt-6">Legal</h6>
        <div className="border border-gray-300 rounded p-4 mt-3">
          <div className="flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1 mb-4">
            <div className="flex-1">Terms of Service</div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 max-[600px]:text-xs">Last update: 23/07/2025</span>
              <Link to="/legal/terms-of-service">
                <button className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors">
                  <ArrowRight size={18} />
                </button>
              </Link>
            </div>
          </div>
          <div className="flex items-center justify-between max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
            <div className="flex-1">Privacy Policy</div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 max-[600px]:text-xs">Last update: 23/07/2025</span>
              <Link to="/legal/privacy-policy">
                <button className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors">
                  <ArrowRight size={18} />
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* change name modal */}
        {nameModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 max-[600px]:px-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md mt-[120px] max-[600px]:mt-[80px]">
              <div className="flex items-center justify-between border-b px-6 py-4">
                <h5 className="text-lg font-semibold">Change Name</h5>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => setNameModalOpen(false)}
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              <form onSubmit={handleNameChange}>
                <div className="px-6 py-4">
                  <div className="mb-3">
                    <label htmlFor="newName" className="block mb-2 font-medium">
                      New Name
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      id="newName"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Enter your name"
                    />
                  </div>

                  {nameError && (
                    <div
                      className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-3"
                      role="alert"
                    >
                      {nameError}
                    </div>
                  )}

                  {nameSuccess && (
                    <div
                      className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-3"
                      role="alert"
                    >
                      {nameSuccess}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 border-t px-6 py-4">
                  <SecondaryButton
                    type="button"
                    onClick={() => setNameModalOpen(false)}
                  >
                    Cancel
                  </SecondaryButton>

                  <PrimaryButton type="submit" disabled={nameLoading}>
                    {nameLoading ? "Updating..." : "Update Name"}
                  </PrimaryButton>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* change password modal */}
        {passwordModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 max-[600px]:px-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md mt-[120px] max-[600px]:mt-[80px]">
              <div className="flex items-center justify-between border-b px-6 py-4">
                <h5 className="text-lg font-semibold">Change Password</h5>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => setPasswordModalOpen(false)}
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              <form onSubmit={handlePasswordChange}>
                <div className="px-6 py-4">
                  <div className="mb-3">
                    <label
                      htmlFor="currentPassword"
                      className="block mb-2 font-medium"
                    >
                      Current Password
                    </label>
                    <input
                      type="password"
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      id="currentPassword"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                    />
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor="newPassword"
                      className="block mb-2 font-medium"
                    >
                      New Password
                    </label>
                    <input
                      type="password"
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor="confirmPassword"
                      className="block mb-2 font-medium"
                    >
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                  </div>

                  {passwordError && (
                    <div
                      className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-3"
                      role="alert"
                    >
                      {passwordError}
                    </div>
                  )}

                  {passwordSuccess && (
                    <div
                      className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-3"
                      role="alert"
                    >
                      {passwordSuccess}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 border-t px-6 py-4">
                  <SecondaryButton
                    type="button"
                    onClick={() => setPasswordModalOpen(false)}
                  >
                    Cancel
                  </SecondaryButton>

                  <PrimaryButton type="submit" disabled={passwordLoading}>
                    {passwordLoading ? "Updating..." : "Update Password"}
                  </PrimaryButton>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Profile;
